import React from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { formatEUR } from "@/lib/constants";
import { Flame } from "lucide-react";

const PHASE_CONFIG = [
  {
    key: "anfrage",
    label: "Anfrage",
    headerBg: "bg-blue-50/60",
    badgeBg: "bg-blue-200",
    badgeText: "text-blue-900",
    chipBorder: "border-blue-100",
    accent: "text-blue-700",
    statusFilter: "Neuer Lead",
  },
  {
    key: "angebot",
    label: "Angebot",
    headerBg: "bg-orange-50/60",
    badgeBg: "bg-orange-200",
    badgeText: "text-orange-900",
    chipBorder: "border-orange-100",
    accent: "text-orange-700",
    chipValueColor: "text-orange-600",
    statusFilter: "Angebot versendet",
  },
  {
    key: "nachfass",
    label: "Nachfass",
    headerBg: "bg-amber-50/60",
    badgeBg: "bg-amber-200",
    badgeText: "text-amber-900",
    chipBorder: "border-amber-100",
    accent: "text-amber-700",
    chipValueColor: "text-amber-700",
    statusFilter: "Nachfassen aktiv",
  },
  {
    key: "auftrag",
    label: "Auftrag",
    headerBg: "bg-emerald-50/60",
    badgeBg: "bg-emerald-200",
    badgeText: "text-emerald-900",
    chipBorder: "border-emerald-100",
    accent: "text-emerald-700",
    chipValueColor: "text-emerald-600",
    statusFilter: "Gewonnen",
  },
];

function LeadChip({ lead, phaseKey, valueColor, borderColor, onClick }) {
  const showValue = phaseKey !== "anfrage" && lead.value > 0;
  const showCity = phaseKey === "anfrage" && lead.city;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`block w-full text-left bg-white rounded-lg p-2.5 shadow-sm border ${borderColor} hover:shadow-md hover:border-slate-300 transition`}
      data-testid={`pipeline-lead-${lead.lead_id}`}
    >
      <div className="flex items-center gap-1.5">
        {lead.is_hot && <Flame className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" />}
        <span className="text-xs font-semibold text-slate-900 truncate">{lead.name || "—"}</span>
      </div>
      <div className="mt-0.5 text-[11px] truncate">
        {showValue && <span className={`font-semibold ${valueColor || "text-slate-600"}`}>{formatEUR(lead.value)}</span>}
        {showCity && <span className="text-slate-500">{lead.city}</span>}
        {!showValue && !showCity && <span className="text-slate-400">&nbsp;</span>}
      </div>
    </button>
  );
}

function PhaseColumn({ phase, data, nav }) {
  const remaining = data.count - data.leads.length;
  return (
    <div
      data-testid={`pipeline-col-${phase.key}`}
      className={`rounded-xl p-3 min-h-[220px] ${phase.headerBg}`}
    >
      <div className="flex items-center justify-between mb-2">
        <span className={`text-[10px] font-bold uppercase tracking-wider ${phase.accent}`}>
          {phase.label}
        </span>
        <span
          className={`${phase.badgeBg} ${phase.badgeText} text-[11px] font-bold px-2 py-0.5 rounded-full tabular-nums`}
          data-testid={`pipeline-count-${phase.key}`}
        >
          {data.count}
        </span>
      </div>
      {data.total_value > 0 && phase.key !== "anfrage" && (
        <div className="text-[11px] font-semibold text-slate-700 mb-2 tabular-nums">
          {formatEUR(data.total_value)}
        </div>
      )}
      <div className="space-y-2">
        {data.leads.length === 0 ? (
          <div className="text-center text-[11px] text-slate-400 py-6 italic">Keine Leads</div>
        ) : (
          data.leads.map((lead) => (
            <LeadChip
              key={lead.lead_id}
              lead={lead}
              phaseKey={phase.key}
              valueColor={phase.chipValueColor}
              borderColor={phase.chipBorder}
              onClick={() => nav(`/leads/${lead.lead_id}`)}
            />
          ))
        )}
        {remaining > 0 && (
          <button
            data-testid={`pipeline-more-${phase.key}`}
            onClick={() => nav(`/leads?status=${encodeURIComponent(phase.statusFilter)}`)}
            className={`block w-full text-center text-[11px] font-semibold ${phase.accent} hover:underline pt-1`}
          >
            + {remaining} weitere →
          </button>
        )}
      </div>
    </div>
  );
}

export default function PipelineKanban({ pipeline, totalValue }) {
  const nav = useNavigate();
  if (!pipeline) return null;

  return (
    <Card className="border-slate-200 p-5 md:p-6" data-testid="pipeline-kanban">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>
            Vertriebs-Pipeline
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Klick auf einen Lead öffnet die Detailseite
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.12em] text-slate-400 font-semibold">Pipeline gesamt</div>
          <div className="text-xl font-bold text-slate-900 tabular-nums" data-testid="pipeline-total-value">
            {formatEUR(totalValue || 0)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {PHASE_CONFIG.map((phase) => (
          <PhaseColumn
            key={phase.key}
            phase={phase}
            data={pipeline[phase.key] || { count: 0, total_value: 0, leads: [] }}
            nav={nav}
          />
        ))}
      </div>
    </Card>
  );
}
